"use client";

import { cn, spacingClasses } from "@/lib/utils";
import React, { useEffect, useState, useCallback, memo, useMemo } from "react";
import { SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { sectionBackgroundColors } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import Sticky from 'react-sticky-el';
import Link from "next/link";
import ScrollSpy from 'react-scrollspy-navigation';
import { useHeaderSheet } from '@/lib/header-sheet-context';
import parse from 'html-react-parser';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ShimmerButton } from "../magicui/shimmer-button";

interface PageNavProps { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  attributes?: React.HTMLProps<HTMLElement>;
  isLoading?: boolean;
}

interface NavButton {
  label: string;
  link: string;
  visible: boolean;
}

interface NavButtonProps {
  label: string;
  link: string;
  isActive: boolean;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>, link: string) => void;
}

const useNavSections = () => {
  const [navButtonsVisible, setNavButtonsVisible] = useState<NavButton[]>([]);
  const [navButtons, setNavButtons] = useState<NavButton[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const findNavSections = () => {
      setIsLoading(true);
      try {
        const sections = document.querySelectorAll('section[data-page-nav="true"], section[data-page-nav="false"]');
        const buttons: NavButton[] = Array.from(sections).map(section => {
          const id = section.id;
          const title = section.getAttribute('data-page-nav-title') || '';
          return {
            label: title,
            link: `#${id}`,
            visible: section.getAttribute('data-page-nav') === 'true'
          };
        });
        setNavButtons(buttons);
        setNavButtonsVisible(buttons.filter(button => button.visible));
      } finally {
        setIsLoading(false);
      }
    };

    // Initial find
    findNavSections();

    // Set up a MutationObserver to watch for DOM changes
    const observer = new MutationObserver(() => {
      findNavSections();
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-page-nav', 'id']
    });

    return () => observer.disconnect();
  }, []);

  return { navButtonsVisible, navButtons, isLoading };
};

const MobileNavDropdown = memo<{ 
  buttons: NavButton[], 
  activeSectionId: string,
  onClick: (e: React.MouseEvent<HTMLAnchorElement>, link: string) => void 
}>(({ buttons, activeSectionId, onClick }) => {
  const activeButton = buttons.find(btn => btn.link.substring(1) === activeSectionId);
  const [open, setOpen] = useState(false);
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="xl:hidden w-full flex justify-between items-center max-w-[100%] sm:max-w-full"
        >
          <span className="truncate">
            {activeButton ? activeButton.label : "Jump to"}
          </span>
          <span className="material-symbols-outlined size-5 !text-[20px]">
            expand_more
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px] z-550">
        {buttons.map((button) => (
          <DropdownMenuItem key={button.link} asChild>
            <Link
              href={button.link}
              onClick={(e) => {
                onClick(e, button.link);
                setOpen(false);
              }}
              className={cn(
                "cursor-pointer",
                button.link.substring(1) === activeSectionId && "font-bold text-primary"
              )}
            >
              {button.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

MobileNavDropdown.displayName = 'MobileNavDropdown';

const MoreDropdown = memo<{ 
  buttons: NavButton[], 
  activeSectionId: string,
  onClick: (e: React.MouseEvent<HTMLAnchorElement>, link: string) => void 
}>(({ buttons, activeSectionId, onClick }) => {
  const activeButton = buttons.find(btn => btn.link.substring(1) === activeSectionId);
  const [open, setOpen] = useState(false);
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={activeButton ? "outline" : "ghost"}
          size="sm" 
          className={cn(
            "paragraph-base font-normal rounded-full shrink-0 transition-colors duration-200 border-2",
            activeButton ? "border-primary font-bold" : "border-transparent",
            "hidden xl:inline-flex" // Only show on xl and above
          )}
        >
          <span className="truncate">
            {activeButton ? activeButton.label : "More"}
          </span>
          <span className="material-symbols-outlined size-5 !text-[20px] ml-1">
            expand_more
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px] z-550">
        {buttons.map((button) => (
          <DropdownMenuItem key={button.link} asChild>
            <Link
              href={button.link}
              onClick={(e) => {
                onClick(e, button.link);
                setOpen(false);
              }}
              className={cn(
                "cursor-pointer",
                button.link.substring(1) === activeSectionId && "font-bold text-primary"
              )}
            >
              {button.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

MoreDropdown.displayName = 'MoreDropdown';

const NavButton = memo<NavButtonProps>(({ 
  label, 
  link,
  isActive,
  onClick,
}) => (
  <Button 
    asChild 
    size="sm" 
    variant={isActive ? "outline" : "ghost"} 
    className={cn(
      "paragraph-base font-normal rounded-full shrink-0 transition-colors duration-200 border-2 border-transparent",
      isActive && "border-primary font-bold",
      "hidden xl:inline-flex" // Hide on mobile
    )}
  >
    <Link href={link} onClick={(e) => onClick(e, link)}>{label}</Link>
  </Button>
), (prevProps: NavButtonProps, nextProps: NavButtonProps) => {
  return prevProps.label === nextProps.label && 
         prevProps.link === nextProps.link &&
         prevProps.isActive === nextProps.isActive;
});

NavButton.displayName = 'NavButton';

// Add skeleton components
const NavButtonSkeleton = memo(() => (
  <div className="h-9 w-20 rounded-full bg-muted animate-pulse hidden xl:block" />
));

NavButtonSkeleton.displayName = 'NavButtonSkeleton';

const MobileNavSkeleton = memo(() => (
  <div className="h-9 w-20 rounded-full bg-muted animate-pulse xl:hidden" />
));

MobileNavSkeleton.displayName = 'MobileNavSkeleton';

const MoreDropdownSkeleton = memo(() => (
  <div className="h-9 w-20 rounded-full bg-muted animate-pulse hidden xl:block" />
));

MoreDropdownSkeleton.displayName = 'MoreDropdownSkeleton';

const PageNav = memo<PageNavProps>(({
  darkMode = false,
  variant = "A",
  spacing = "none",
  attributes,
}: PageNavProps) => {
  const { key } = attributes ?? {};
  const spacingClass = spacingClasses[spacing || 'none'];
  const [isSticky, setIsSticky] = useState(false);
  const { navButtonsVisible, navButtons, isLoading } = useNavSections();
  const [activeId, setActiveId] = useState<string>("");
  const [isClient, setIsClient] = useState(false);
  const { openSheet, accountArea, customAccountArea } = useHeaderSheet();

  // Add client-side check
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Add effect to manage body class based on sticky state
  useEffect(() => {
    if (isSticky) {
      document.body.classList.add('page-nav-sticky');
    } else {
      document.body.classList.remove('page-nav-sticky');
    }
    return () => {
      document.body.classList.remove('page-nav-sticky');
    };
  }, [isSticky]);

  const { listButtons, moreButtons } = useMemo(() => {
    if (navButtonsVisible.length <= 4) {
      return { listButtons: navButtonsVisible, moreButtons: [] };
    }
    return {
      listButtons: navButtonsVisible.slice(0, 4), // Show first 4 buttons
      moreButtons: navButtonsVisible.slice(4) // Put rest in more dropdown
    };
  }, [navButtonsVisible]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, link: string) => {
    e.preventDefault();
    const element = document.querySelector(link);
    if (!element) return;

    const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementTop - 72;
    const scrollDistance = Math.abs(window.scrollY - offsetPosition);

    window.scrollTo({
      top: offsetPosition,
      behavior: scrollDistance > 100 ? "smooth" : "auto"
    });
  }, []);

  const sectionClass = useMemo(() => cn(
    spacingClass,
    darkMode ? 'dark' : 'light',
    sectionBackgroundColors(darkMode, variant),
    'relative z-200 text-foreground !pt-0 transition-all duration-200 border-b border-t will-change-transform',
    "[html:has([data-slot=navigation-menu-viewport][data-state=open])_&]:z-30",
    // Safari-specific optimizations
    "[-webkit-transform:translateZ(0)] [transform:translateZ(0)]", // Force hardware acceleration
    "[-webkit-backface-visibility:hidden] [backface-visibility:hidden]", // Prevent flickering
    "[-webkit-perspective:1000px] [perspective:1000px]", // Enable 3D rendering
  ), [spacingClass, darkMode, variant]);

  return (
    <>
      <Sticky 
        key={key}
        wrapperClassName={sectionClass}
        // topOffset={-72}
        topOffset={0}
        stickyClassName="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm !border-transparent z-30 translate-y-[0px]"
        onFixedToggle={(fixed) => {
          setIsSticky(fixed);
        }}
        hideOnBoundaryHit={false}
        disabled={false}
        positionRecheckInterval={16}
        stickyStyle={{
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
          WebkitPerspective: '1000px',
          perspective: '1000px',
        }}
      >
        <div className="container flex flex-row justify-between py-3 gap-2 sm:gap-10 lg:gap-0 h-[70px]">
          <div className="flex flex-row items-center gap-3 max-w-[60%] sm:max-w-full">
            <span className="hidden md:flex paragraph-base font-bold lg:pr-6 items-center whitespace-nowrap shrink-0 h-11">
              On this page
            </span>
            {isLoading ? (
              <>
                {[...Array(4)].map((_, index) => (
                  <NavButtonSkeleton key={`skeleton-${index}`} />
                ))}
                {moreButtons && moreButtons.length > 0 && <MoreDropdownSkeleton />}
                <MobileNavSkeleton />
              </>
            ) : (
              navButtons && navButtons.length > 0 && isClient && (
                <div className="flex gap-4 min-w-0 items-center">
                  <ScrollSpy
                    key={key}
                    activeClass="nav-active" 
                    rootMargin='300px'
                    activeAttr
                    onChangeActiveId={(id: string) => {
                      setActiveId(id);
                    }}
                  >
                    {navButtons.map((button: NavButton) => (
                      <a
                        key={button.link}
                        className="hidden"
                        href={button.link}
                        aria-label={button.label}
                      />
                    ))}
                  </ScrollSpy>
                  <div className="flex gap-4 max-w-full">
                    {listButtons && listButtons.length > 0 && (
                      listButtons.map((button: NavButton) => (
                        button.visible && (
                          <NavButton
                            key={button.link}
                            label={button.label}
                            link={button.link}
                            isActive={activeId === button.link.substring(1)}
                            onClick={handleClick}
                          />
                        )
                      ))
                    )}
                    {moreButtons && moreButtons.length > 0 && (
                      <MoreDropdown
                        buttons={moreButtons}
                        activeSectionId={activeId}
                        onClick={handleClick}
                      />
                    )}
                    <MobileNavDropdown
                      buttons={navButtonsVisible}
                      activeSectionId={activeId}
                      onClick={handleClick}
                    />
                  </div>
                </div>
              )
            )}
          </div>

          <div className={cn(
            "flex items-center gap-3 transition-opacity duration-300",
            isSticky ? "opacity-100" : "opacity-0"
          )}>
            {customAccountArea?.customAccountArea ? (
              
              <div className="gap-2 flex">
                {customAccountArea?.customText && <span className="hidden md:flex xl:hidden 2xl:flex items-center paragraph-base mr-2 text-foreground">{parse(customAccountArea?.customText)}</span>}
                {customAccountArea?.customButtonText && (
                  <Button asChild variant={customAccountArea?.customButtonStyle} size="sm">
                    <Link href={customAccountArea?.customButtonLink || "/"}>{customAccountArea?.customButtonText}</Link>
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="gap-2 2xl:w-60 justify-end hidden xl:flex">
                  {accountArea?.loginButton && (
                    <Button asChild variant="ghost" size="sm">
                      <Link className="paragraph-sm font-normal flex items-center gap-2 z-10" href={accountArea.loginLink || "/"}>
                        <span className="material-symbols-outlined">login</span>
                        {accountArea.loginText}
                      </Link>                  
                    </Button>
                  )}
                  {accountArea?.enquiryButton && (
                    <ShimmerButton>
                      <Link className="paragraph-sm font-bold text-nowrap z-10" href={accountArea.enquiryLink || "/"}>{accountArea.enquiryText}</Link>
                    </ShimmerButton>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={openSheet} className="xl:hidden z-10">
                  <span className="material-symbols-outlined text-primary">menu</span>
                </Button>
              </>
            )}
            
          </div>
        </div>
      </Sticky>
      <div className="hidden"></div>
    </>
    );
  }, (prevProps: PageNavProps, nextProps: PageNavProps) => {
    return prevProps.darkMode === nextProps.darkMode &&
           prevProps.variant === nextProps.variant &&
           prevProps.spacing === nextProps.spacing &&
           prevProps.attributes?.key === nextProps.attributes?.key;
});

PageNav.displayName = 'PageNav';

export { PageNav };


